import React, { useEffect, useState } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
  ReferenceArea,
} from "recharts";
import {
  Card,
  CardBody,
  CardTitle,
  Spinner,
  Row,
  Col,
  Input,
  Button,
} from "reactstrap";
import axiosInstance from "../../components/AxiosInstance";

const API_BASE_URL = "https://api.hvacsync.in/api";

const InputPressureChart = ({compressorId}) => {
  const today = new Date().toISOString().split("T")[0];

  const [selectedDate, setSelectedDate] = useState(today);
  const [appliedDate, setAppliedDate] = useState(today);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  // 🔹 Generate full 24hr timeline
  const generateFullDayTimeline = () => {
    const times = [];
    for (let h = 0; h < 24; h++) {
      for (let m = 0; m < 60; m++) {
        const hh = String(h).padStart(2, "0");
        const mm = String(m).padStart(2, "0");
        times.push(`${hh}:${mm}`);
      }
    }
    return times;
  };

  const fetchPressureData = async (date) => {
    try {
      setLoading(true);

      const res = await axiosInstance.get(
        `${API_BASE_URL}/input-pressure/timeline/?date=${date}`
      );

      const apiTimeline = res.data?.timeline || [];

      // Convert API array to map for fast lookup
      const apiMap = {};
      apiTimeline.forEach((item) => {
        if (item.input_pressure !== null) {
          apiMap[item.time] = item.input_pressure;
        }
      });

      const fullTimeline = generateFullDayTimeline();

      const now = new Date();
      const currentTime = `${String(now.getHours()).padStart(2, "0")}:${String(
        now.getMinutes()
      ).padStart(2, "0")}`;

      const isToday = date === today;

      const merged = fullTimeline.map((time) => {
        let value = apiMap[time] ?? null;

        // 🔹 mark future data
        let isFuture = false;
        if (isToday && time > currentTime) {
          isFuture = true;
          value = null;
        }

        return {
          time,
          pressure: value,
          isFuture,
        };
      });

      setData(merged);
    } catch (err) {
      console.error("Error fetching pressure data:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPressureData(appliedDate);
  }, [appliedDate]);

  const handleSubmit = () => {
    setAppliedDate(selectedDate || today);
  };

  return (
    <Card className="shadow-sm mb-4">
      <CardBody>
        <CardTitle tag="h5">Inline Pressure Trend</CardTitle>

        {/* 🔹 Date Picker */}
        <Row className="mb-3">
          <Col md={4}>
            <Input
              type="date"
              value={selectedDate}
              max={today}
              onChange={(e) => setSelectedDate(e.target.value)}
            />
          </Col>
          <Col md={2}>
            <Button color="primary" onClick={handleSubmit}>
              Load
            </Button>
          </Col>
        </Row>

        {loading ? (
          <div className="text-center py-4">
            <Spinner />
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={320}>
            <LineChart data={data}>
              <CartesianGrid strokeDasharray="3 3" />

              <XAxis
                dataKey="time"
                interval={60} // every 1 hour
                tick={{ fontSize: 10 }}
              />

              <YAxis
                label={{
                  value: "Pressure",
                  angle: -90,
                  position: "insideLeft",
                }}
              />

              <Tooltip />

              {/* 🔹 Main Data Line */}
              <Line
                type="monotone"
                dataKey="pressure"
                stroke="#007bff"
                dot={false}
                connectNulls={false}
              />

              {/* 🔹 Future Area (Grey overlay) */}
              {appliedDate === today && (
                <ReferenceArea
                  x1={
                    data.find((d) => d.isFuture)?.time
                  }
                  x2="23:59"
                  strokeOpacity={0}
                  fill="rgba(0,0,0,0.1)"
                />
              )}
            </LineChart>
          </ResponsiveContainer>
        )}
      </CardBody>
    </Card>
  );
};

export default InputPressureChart;